import { Alert, Anchor, Stack } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clickTrackingVerified from '../helpers/clickTrackingVerified';
import useFetch from '../helpers/useFetch';
import ErrorMessage from './ErrorMessage';

// The two sender-domain DNS records gate different things, so they are reported
// separately: the click-tracking CNAME wraps the links of every channel and its
// absence stops all campaign sending, while DKIM only signs mail.
//
// This alert has nine mount sites and only ever reads the polled verificationStatus.
// The click host's end-to-end health is a live outbound probe, so it is fetched where
// the merchant can act on it - the messaging general tab and the verification modal -
// and this alert points there rather than probing on every page mount.
const SenderVerificationAlert = () => {
  const { domain } = useSelector((state) => state.domain);
  // A Klaviyo-connected domain delivers its *email* through Klaviyo, so Releva's DKIM
  // record is irrelevant to it and the email warning would be misleading noise. Its
  // other channels still go through Releva and still wrap links through the click
  // host, so the click-tracking warning applies to it like any other domain. Gated on
  // the slice's loading flag: while the Klaviyo state for THIS domain is unknown, say
  // nothing about email (no false alarms on Klaviyo domains, no suppressed alarms on
  // plain ones).
  const { account: klaviyoAccount, loading: klaviyoLoading } = useSelector((state) => state.klaviyo);
  const [verificationStatus, setVerificationStatus] = useState();
  const [error, setError] = useState();
  const fetch = useFetch();

  useEffect(() => {
    setVerificationStatus(undefined);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/verificationStatus`)
      .then(setVerificationStatus)
      .catch(setError);
  }, [domain.accountId, domain.id, fetch]);

  if (error !== undefined) {
    return <ErrorMessage message={error.message} />;
  }

  if (verificationStatus === undefined) {
    return null;
  }

  const settingsHref = (tab) =>
    `#/accounts/${domain.accountId}/domains/${domain.id}/settings/messagingAndAds?tab=${tab}`;
  const clickTrackingMissing = clickTrackingVerified(verificationStatus) === false;
  const dkimMissing =
    klaviyoLoading === false &&
    !klaviyoAccount &&
    !verificationStatus.isEmailSendingVerified &&
    !verificationStatus.releva?.dkim?.valid;

  if (!clickTrackingMissing && !dkimMissing) {
    return null;
  }

  return (
    <Stack gap="xs">
      {clickTrackingMissing && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Campaign sending is disabled until the click-tracking DNS record is in place for your sender domain - links in
          every channel are wrapped through it. The exact value to add, and a live check of whether that host is
          serving, are in your{' '}
          <Anchor href={settingsHref('general')} fw={500}>
            Shop Settings
          </Anchor>
          .
        </Alert>
      )}
      {dkimMissing && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Email sending is disabled until the DKIM DNS record is in place for your sender domain. The exact value to add
          is in your{' '}
          <Anchor href={settingsHref('emails')} fw={500}>
            Shop Settings
          </Anchor>
          .
        </Alert>
      )}
    </Stack>
  );
};

export default SenderVerificationAlert;
